import axios from 'axios';

const TOKEN_KEY = 'samvad_admin_token';

export const getToken = () => {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
};

export const setToken = (token) => {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* noop */ }
};

const api = axios.create({
  baseURL: '/api',
  withCredentials: true,
  timeout: 60_000,
});

// api.interceptors.request.use((config) => {
//   const token = getToken();
//   if (token) {
//     config.headers = config.headers || {};
//     config.headers.Authorization = `Bearer ${token}`;
//   }
//   return config;
// });

let onUnauthorized = null;
export const setUnauthorizedHandler = (fn) => { onUnauthorized = fn; };

api.interceptors.response.use(
  (resp) => resp,
  (err) => {
    if (err?.response?.status === 401 && onUnauthorized) onUnauthorized();
    return Promise.reject(err);
  }
);

// ============================================================
// Auth
// ============================================================
export const authApi = {
  // Fresh session data from the server
  me: () => api.get('/auth/me').then(r => r.data),
  // Direct redirect to the backend's login endpoint
  login: () => { window.location.href = '/api/auth/login'; },
  // Server-side logout
  logout: () => api.post('/auth/logout').then(r => r.data),
};

// ============================================================
// Dashboard / Analytics
// ============================================================
export const dashboardApi = {
  headline: (params) => api.get('/admin/dashboard/headline', { params }).then(r => r.data),
  timeseries: (params) => api.get('/admin/dashboard/timeseries', { params }).then(r => r.data),
  topics: (params) => api.get('/admin/dashboard/topics', { params }).then(r => r.data),
  themes: (params) => api.get('/admin/dashboard/themes', { params }).then(r => r.data),
  kmsRefs: (params) => api.get('/admin/dashboard/kms-references', { params }).then(r => r.data),
  kmsGaps: (params) => api.get('/admin/dashboard/kms-gaps', { params }).then(r => r.data),
  unanswered: (params) => api.get('/admin/dashboard/unanswered', { params }).then(r => r.data),
  sessions: ({ page = 1, limit = 20 } = {}) =>
    api.get('/admin/user-sessions', { params: { page, limit } }).then(r => r.data),
};
export const alphaDashboardApi = {
  headline: (params) => api.get('/admin/alpha-dashboard/headline', { params }).then(r => r.data),
  timeseries: (params) => api.get('/admin/alpha-dashboard/timeseries', { params }).then(r => r.data),
  topics: (params) => api.get('/admin/alpha-dashboard/topics', { params }).then(r => r.data),
  themes: (params) => api.get('/admin/alpha-dashboard/themes', { params }).then(r => r.data),
  kmsRefs: (params) => api.get('/admin/alpha-dashboard/kms-references', { params }).then(r => r.data),
  unanswered: (params) => api.get('/admin/alpha-dashboard/unanswered', { params }).then(r => r.data),
  evaluationsSummary: (params) => api.get('/admin/alpha-dashboard/evaluations-summary', { params }).then(r => r.data),
  stats: (params) => api.get('/admin/alpha-dashboard/stats', { params }).then(r => r.data),
};

export const userSessionsApi = {
  listForUser: (userId, params) =>
    api.get(`/admin/user-sessions/${userId}/conversations`, { params }).then(r => r.data),
};

export const answerDetailClicksApi = {
  users: (params) => api.get('/admin/conversations/detailed-answer-clicks/users', { params }).then(r => r.data),
  listForUser: (userId, params) =>
    api.get(`/admin/conversations/detailed-answer-clicks/users/${encodeURIComponent(userId)}`, { params }).then(r => r.data),
};

// ============================================================
// Conversations
// ============================================================
export const conversationsApi = {
  list: (params) => api.get('/admin/conversations', { params }).then(r => r.data),
  users: (params) => api.get('/admin/conversations/users-summary', { params }).then(r => r.data),
  exportMessages: (params) => api.get('/admin/conversations/export', { params, responseType: 'blob' }).then(r => r.data),
  detail: (id) => api.get(`/admin/conversations/${id}`).then(r => r.data),
  summarise: (id) => api.post(`/admin/conversations/${id}/summarise`).then(r => r.data),
  flagged: (params) => api.get('/admin/conversations/_/flagged', { params }).then(r => r.data),
  reviewFlag: (convId, msgId, body) =>
    api.patch(`/admin/conversations/${convId}/messages/${msgId}/flag-review`, body).then(r => r.data),
};

// ============================================================
// Alpha Conversations
// ============================================================
export const alphaConversationsApi = {
  list: (params) => api.get('/admin/alpha-conversations', { params }).then(r => r.data),
  detail: (id) => api.get(`/admin/alpha-conversations/${id}`).then(r => r.data),
  evaluations: (id) => api.get(`/admin/alpha-conversations/${id}/evaluations`).then(r => r.data),
  reviewFlag: (convId, msgId, body) =>
    api.patch(`/admin/alpha-conversations/${convId}/messages/${msgId}/flag-review`, body).then(r => r.data),
};

// ============================================================
// KMS
// ============================================================
export const kmsApi = {
  references: (params) => api.get('/admin/kms/references', { params }).then(r => r.data),
  reference: (id) => api.get(`/admin/kms/references/${id}`).then(r => r.data),
  gaps: (params) => api.get('/admin/kms/gaps', { params }).then(r => r.data),
  createGap: (body) => api.post('/admin/kms/gaps', body).then(r => r.data),
  updateGap: (id, body) => api.patch(`/admin/kms/gaps/${id}`, body).then(r => r.data),
  deleteGap: (id) => api.delete(`/admin/kms/gaps/${id}`).then(r => r.data),
};

// ============================================================
// Users
// ============================================================
export const usersApi = {
  list: (params) => api.get('/admin/users', { params }).then(r => r.data),
  roles: () => api.get('/admin/users/roles').then(r => r.data),
  setRole: (id, role_id) => api.patch(`/admin/users/${id}/role`, { role_id }).then(r => r.data),
  setActive: (id, is_active) => api.patch(`/admin/users/${id}/active`, { is_active }).then(r => r.data),
  audit: (params) => api.get('/admin/users/audit', { params }).then(r => r.data),
};

// ============================================================
// AI Assistant
// ============================================================
export const assistantApi = {
  ask: (body) => api.post('/admin/assistant/ask', body).then(r => r.data),
  history: () => api.get('/admin/assistant/history').then(r => r.data),
  reset: () => api.post('/admin/assistant/reset').then(r => r.data),
};

export default api;
